import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useT } from "../../lib/texte";
import { Icon } from "./Icon";
import { IconButton, portalRoot } from "./primitives";

export interface ModalProps {
  open: boolean;
  onClose?: () => void;
  children: ReactNode;
  /** Breite am Schreibtisch; auf dem Telefon geht das Blatt auf volle Breite. */
  width?: number;
  title?: string;
  /** Name aus der Icon-Registry — links vom Titel, auf dem Telefon ausgeblendet */
  icon?: string;
  subtitle?: string;
  footer?: ReactNode;
  closeOnBackdrop?: boolean;
  /** Aktionen in der Kopfzeile links vom X */
  headerExtra?: ReactNode;
}

/**
 * Das Modal.
 *
 * Drei Dinge, die es tut und die man leicht vergisst:
 *  1. Escape schließt — der Horcher hängt am Fenster und nur, solange es offen
 *     ist. Das Auswahlfeld in primitives.tsx verbraucht sein eigenes Escape
 *     (stopPropagation), damit eine offene Liste nicht das Modal mitnimmt.
 *  2. Der Fokus WANDERT hinein und beim Schließen zurück. Ohne das steht der
 *     Fokus einer Vorlesehilfe weiter auf dem Knopf dahinter, und die
 *     Tabulatorreise führt durch eine Seite, die niemand sieht.
 *  3. Die Seite darunter rollt nicht mit. Auf dem Telefon ist das der
 *     Unterschied zwischen einem Blatt und einem Durcheinander.
 *
 * Auf dem Telefon steigt es vom unteren Rand auf und ist deckend statt
 * durchscheinend (`.modal-wrap`/`.modal-card` in base.css).
 */
export function Modal({ open, onClose, children, width = 520, title, icon, subtitle, footer, closeOnBackdrop = true, headerExtra }: ModalProps) {
  const { t } = useT();
  const blattRef = useRef<HTMLDivElement>(null);
  const vorherRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    vorherRef.current = document.activeElement;
    // Das Blatt selbst bekommt den Fokus (tabIndex -1) und nicht das erste
    // Feld darin: ein Modal, das mit einem markierten Eingabefeld aufgeht,
    // verschluckt auf dem Telefon die erste Tastatureingabe.
    blattRef.current?.focus({ preventScroll: true });
    const alt = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = alt;
      const zurueck = vorherRef.current;
      if (zurueck instanceof HTMLElement) zurueck.focus({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="anim-fade modal-wrap"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      <div
        onClick={() => closeOnBackdrop && onClose && onClose()}
        style={{ position: "absolute", inset: 0, background: "rgba(8,8,8,0.5)", backdropFilter: "blur(4px)" }}
      />
      <div
        ref={blattRef}
        tabIndex={-1}
        className="card anim-pop scroll modal-card"
        // dvh statt vh: die Browserleisten eines Telefons schrumpfen den
        // sichtbaren Bereich — mit 90vh ragte das mittige Blatt oben UND unten
        // aus dem Schirm.
        style={{
          position: "relative",
          width,
          maxWidth: "100%",
          maxHeight: "min(90vh, calc(100dvh - 32px))",
          overflowY: "auto",
          borderRadius: "var(--r-knopf)",
          boxShadow: "var(--shadow-lg)",
          outline: "none",
        }}
      >
        {title && (
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, padding: "22px 24px 16px" }}>
            <div style={{ display: "flex", gap: 13 }}>
              {icon && (
                <div
                  className="mobile-hide"
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: "var(--r-knopf)",
                    background: "var(--accent-soft)",
                    color: "var(--accent-text)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "none",
                  }}
                >
                  <Icon name={icon} size={20} />
                </div>
              )}
              <div>
                <h3 className="disp" style={{ fontSize: 19 }}>{title}</h3>
                {subtitle && <p style={{ fontSize: 13.5, color: "var(--text-mute)", marginTop: 3 }}>{subtitle}</p>}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flex: "none" }}>
              {headerExtra}
              <IconButton icon="x" size={18} box={34} onClick={onClose} title={t.allgemein.schliessen} />
            </div>
          </div>
        )}
        <div style={{ padding: title ? "0 24px 22px" : 24 }}>{children}</div>
        {footer && (
          <div
            className="modal-foot"
            style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 10, padding: "16px 24px", borderTop: "1px solid var(--glass-border)" }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    portalRoot(),
  );
}
